import { useEffect, useState } from 'react';
import { api, auth } from '../api';
import { run, useAsync } from '../hooks';
import type { EditorProps } from '../pages/SitePage';
import { StatusBadge } from './StatusBadge';

/** Pages the scan reached, updating live while a scan runs; the full re-scan starts from here (FR-C-04). */
export function PagesTable({ bundle, reload, show }: EditorProps) {
  const { site, scans } = bundle;
  const latest = scans[0];
  const scanning = !!latest && !['ready', 'failed'].includes(latest.status);
  const { data, loading, error, reload: refetch } = useAsync(() => api.pages(site.siteId), [site.siteId]);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState(site.guidance?.notes ?? '');
  const [guideOpen, setGuideOpen] = useState(!!site.guidance?.notes);
  const dirty = notes.trim() !== (site.guidance?.notes ?? '');
  const saveGuidance = async () => {
    const r = await run(api.patchSite(site.siteId, { guidance: { notes } }), show, notes.trim() ? 'Guidance saved. It applies to the next scan.' : 'Guidance cleared.');
    if (r) await reload();
  };

  // Live view: refresh every 3 s while a scan runs, every 10 s otherwise so a scan started elsewhere
  // (console on another tab, the daily scheduler) is noticed and the site header catches up.
  const live = scanning || !!data?.running;
  useEffect(() => {
    const t = setInterval(() => void refetch(), live ? 3000 : 10000);
    return () => clearInterval(t);
  }, [live, refetch]);
  useEffect(() => {
    if (data?.running && !scanning) void reload();
  }, [data?.running, scanning, reload]);

  const rescan = async () => {
    if (!confirm('Run a full re-scan? AI-detected categories and cards will be refreshed as a draft; your manual edits are kept.')) return;
    setBusy(true);
    const r = await run(api.rescanAll(site.siteId), show, 'Full re-scan started');
    setBusy(false);
    if (r) {
      await reload();
      await refetch();
    }
  };

  const rows = [...(data?.pages ?? [])].sort((a, b) => Number(b.current) - Number(a.current) || a.depth - b.depth || a.url.localeCompare(b.url));
  const current = rows.filter((p) => p.current);
  const analysed = current.filter((p) => p.stage === 'classified').length;
  const failed = current.filter((p) => p.stage === 'failed').length;

  return (
    <div className="grid" style={{ gap: 14 }}>
      <div className="card row spread">
        <div>
          <div className="row">
            <h2>Pages</h2>
            {latest && <StatusBadge status={latest.status} />}
            {scanning && <span className="badge accent">live</span>}
          </div>
          <p className="small muted" style={{ margin: '6px 0 0' }}>
            {latest ? latest.status === 'failed' ? latest.error : latest.message : 'No scan yet.'}
            {current.length > 0 && ` · ${current.length} page${current.length === 1 ? '' : 's'} captured, ${analysed} analysed${failed ? `, ${failed} failed` : ''}`}
            {latest && auth.token && ` · ${latest.analyser} · US$${(latest.costUsd ?? 0).toFixed(2)}`}
          </p>
        </div>
        <div className="row">
          <button className="btn sm" onClick={() => setGuideOpen((o) => !o)} aria-expanded={guideOpen}>
            {site.guidance?.notes ? 'Guidance for the AI ✓' : 'Guidance for the AI'}
          </button>
          <button className="btn primary sm" onClick={rescan} disabled={busy || scanning}>
            {scanning ? 'Scanning…' : 'Full re-scan'}
          </button>
        </div>
      </div>
      {guideOpen && (
        <div className="card grid" style={{ gap: 10 }}>
          <div>
            <h3>Guidance for the AI (optional)</h3>
            <p className="small muted" style={{ margin: '4px 0 0' }}>
              Plain-language notes the AI reads when it classifies pages and builds the categories, cards and questions. Use it to name a category, group or exclude pages, say who the
              visitors are, or point out what matters most. It can steer what the scan found but cannot add pages that were not crawled. Applies to the next full scan, category re-scan or daily re-scan.
            </p>
          </div>
          <textarea
            className="textarea"
            rows={5}
            maxLength={1500}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={'Examples:\n• Our visitors are fleet buyers and private buyers; make those two separate paths.\n• Group servicing, warranty and roadside assistance into one category called "Own a Subaru".\n• Never feature news or careers pages.\n• The most important action is booking a test drive.'}
          />
          <div className="row spread">
            <span className="small muted">{notes.length}/1500{site.guidance?.updatedAt ? ` · last saved ${new Date(site.guidance.updatedAt).toLocaleString()}` : ''}</span>
            <button className="btn sm primary" onClick={saveGuidance} disabled={!dirty}>
              Save guidance
            </button>
          </div>
        </div>
      )}
      {error && <p className="badge bad">{error}</p>}
      {loading && !data && <p className="muted">Loading…</p>}
      <div className="card" style={{ padding: 0 }}>
        <table>
          <thead>
            <tr>
              <th></th>
              <th>Page</th>
              <th>Type</th>
              <th>CTAs</th>
              <th>Lead points</th>
              <th>Stage</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.url} style={p.current ? undefined : { opacity: 0.55 }}>
                <td>{p.thumbnail ? <img src={p.thumbnail} alt="" style={{ width: 72, borderRadius: 6, display: 'block' }} /> : null}</td>
                <td>
                  <div>{p.cardTitle || p.title || p.url}</div>
                  <a className="small muted mono" href={p.url} target="_blank" rel="noopener">
                    {p.url}
                  </a>
                </td>
                <td>{p.pageType ?? '—'}</td>
                <td>{p.stage === 'classified' ? p.ctas : '—'}</td>
                <td>{p.stage === 'classified' ? p.leads : '—'}</td>
                <td>
                  {p.stage === 'failed' ? (
                    <span className="badge bad" title={p.error ?? `HTTP ${p.statusCode}`}>failed</span>
                  ) : p.stage === 'classified' ? (
                    <span className="badge ok">analysed</span>
                  ) : (
                    <span className="badge accent">captured</span>
                  )}
                  {!p.current && <span className="small muted" style={{ marginLeft: 6 }}>previous scan</span>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan={6} className="muted">
                  No pages recorded yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
